/**
 * Aggregated analytics endpoints over the canonical activity-event taxonomy,
 * plus the typed dashboard bundles.
 */

import type {
  OverviewBundle,
  ErrorAnalyticsResponse,
  ToolsBundle,
  SessionsBundle,
  CostsBundle,
  TrendsBundle,
} from '@/types/analytics/bundles'
import type { AgentToolsAnalyticsResponse } from '@/types/analytics/agentTools'
import type { ContextCompactSummary } from '@/types/analytics/context'
import type { NotificationSummary } from '@/types/analytics/notification'
import type { PermissionAnalyticsResponse } from '@/types/analytics/permission'
import type { SessionBoundedDuration } from '@/types/analytics/session'
import type { SubagentAnalyticsResponse } from '@/types/analytics/subagent'
import { apiClient } from './client'

export const analytics = {
  /**
   * Fetch subagent analytics over the last `days`, from the canonical
   * agent.subagent_start / agent.subagent_stop events.
   *
   * Backend owns the 30-day default and the 1–365 bounds every
   * canonical-event rollup shares (`ANALYTICS_WINDOW_BOUNDS`); `days` is
   * sent only when supplied.
   */
  getSubagentAnalytics: async (
    params: { project_id?: string; days?: number } = {}
  ): Promise<SubagentAnalyticsResponse> => {
    const response = await apiClient.get<SubagentAnalyticsResponse>(
      '/api/v1/analytics/subagents/performance',
      { params }
    )
    return response.data
  },

  /**
   * Fetch permission analytics aggregated over canonical
   * `permission.request` events. Returns per-permission_type counts
   * and per-resource breakdowns surfaced from
   * `details.permission_type` and `details.resource`, over the last `days`
   * (backend default 30).
   */
  getPermissionAnalytics: async (
    params: { project_id?: string; days?: number } = {}
  ): Promise<PermissionAnalyticsResponse> => {
    const response = await apiClient.get<PermissionAnalyticsResponse>(
      '/api/v1/analytics/permissions',
      { params }
    )
    return response.data
  },

  /**
   * Fetch context-compact analytics aggregated over canonical
   * `context.compact` events. Returns total compactions, sessions
   * with compactions, avg compactions per session, and auto/manual
   * trigger breakdown from `details.trigger`, over the last `days` (backend
   * default 30).
   */
  getContextCompactAnalytics: async (
    params: { project_id?: string; days?: number } = {}
  ): Promise<ContextCompactSummary> => {
    const response = await apiClient.get<ContextCompactSummary>(
      '/api/v1/analytics/context',
      { params }
    )
    return response.data
  },

  /**
   * Fetch notification analytics aggregated over canonical
   * `notification` events. Returns total notifications and
   * per-(notification_type, severity) counts, over the last `days` (backend
   * default 30).
   */
  getNotificationAnalytics: async (
    params: { project_id?: string; days?: number } = {}
  ): Promise<NotificationSummary> => {
    const response = await apiClient.get<NotificationSummary>(
      '/api/v1/analytics/notifications',
      { params }
    )
    return response.data
  },

  /**
   * Fetch exact session durations from canonical session.start /
   * session.end event pairs. Sessions without a session.end are excluded,
   * and so are sessions that did not both start and end inside the last
   * `days`. Backend owns the 50-item and 30-day defaults; each is sent only
   * when supplied.
   */
  getSessionBoundedDurations: async (
    params: { project_id?: string; limit?: number; days?: number } = {}
  ): Promise<SessionBoundedDuration[]> => {
    const response = await apiClient.get<SessionBoundedDuration[]>(
      '/api/v1/analytics/sessions/bounded-durations',
      { params }
    )
    return response.data
  },

  /**
   * Fetch how agents use the Alveary MCP tools over the last `days`: each
   * tool's calls and outcomes, command-line fallbacks, wrong first choices and
   * Claude Code's tool search, with the rules each part is derived by. The
   * backend owns the 7-day default and the 1–365 bounds
   * (`ANALYTICS_WINDOW_BOUNDS`); `project_id` narrows the answer to one project.
   */
  getAgentToolsAnalytics: async (
    params: { days?: number; project_id?: string } = {}
  ): Promise<AgentToolsAnalyticsResponse> => {
    const response = await apiClient.get<AgentToolsAnalyticsResponse>(
      '/api/v1/analytics/agent-tools',
      { params }
    )
    return response.data
  },

  // ── Bundle endpoints (Phase D) ──────────────────────────────────────────
  // Each routes through this shared client so auth interceptors and the
  // ApiError normalizer apply — the old inline-axios hooks bypassed both.

  getOverviewBundle: async (params: {
    days?: number
    tz?: string
    project_id?: string
  } = {}): Promise<OverviewBundle> => {
    const response = await apiClient.get<OverviewBundle>(
      '/api/v1/analytics/overview',
      { params }
    )
    return response.data
  },

  getErrorsBundle: async (params: {
    days?: number
    project_id?: string
  } = {}): Promise<ErrorAnalyticsResponse> => {
    const response = await apiClient.get<ErrorAnalyticsResponse>(
      '/api/v1/analytics/errors',
      { params }
    )
    return response.data
  },

  getToolsBundle: async (params: {
    days?: number
    project_id?: string
  } = {}): Promise<ToolsBundle> => {
    const response = await apiClient.get<ToolsBundle>(
      '/api/v1/analytics/tools',
      { params }
    )
    return response.data
  },

  getSessionsBundle: async (params: {
    days?: number
    project_id?: string
  } = {}): Promise<SessionsBundle> => {
    const response = await apiClient.get<SessionsBundle>(
      '/api/v1/analytics/sessions',
      { params }
    )
    return response.data
  },

  getCostsBundle: async (params: {
    days?: number
    project_id?: string
  } = {}): Promise<CostsBundle> => {
    const response = await apiClient.get<CostsBundle>(
      '/api/v1/analytics/costs',
      { params }
    )
    return response.data
  },

  getTrendsBundle: async (params: {
    months?: number
    project_id?: string
  } = {}): Promise<TrendsBundle> => {
    const response = await apiClient.get<TrendsBundle>(
      '/api/v1/analytics/trends',
      { params }
    )
    return response.data
  },
}
